/* eslint-disable consistent-return */
import React, {FC, useEffect, useMemo, useState} from 'react';
import {AdSlot, DFPManager, Size, SizeMapping} from 'react-dfp';
import {createPortal} from 'react-dom';
import {adUnitToDivId} from 'utils/stringTools';

import './styles.scss';

const isViewable = (id: string) => {
  const element = document.getElementById(id);
  if (!element) return false;

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const box = element.getBoundingClientRect();

  /* eslint-disable no-nested-ternary */
  const boxTop = box.top > 0 ? (box.top < viewportHeight ? box.top : viewportHeight) : 0;
  const boxBottom =
    box.bottom > 0 ? (box.bottom < viewportHeight ? box.bottom : viewportHeight) : 0;
  const boxLeft = box.left > 0 ? (box.left < viewportWidth ? box.left : viewportWidth) : 0;
  const boxRight = box.right > 0 ? (box.right < viewportWidth ? box.right : viewportWidth) : 0;

  const viewableArea = (boxRight - boxLeft) * (boxBottom - boxTop);
  const totalArea = box.width * box.height;

  const forceRefresh = totalArea === 0;

  const isTabActive = !document.hidden;

  return (isTabActive && forceRefresh) || (isTabActive && viewableArea / totalArea >= 0.5);
}; // end of isViewable function

interface Props {
  adUnit: string;
  divId?: string;
  scope: string;
  sizes: Size[];
  sizeMapping?: SizeMapping[];
  stuck?: boolean;
  sticky?: boolean;
  autoRefresh?: boolean;
  root?: HTMLElement | null;
}

const Ad: FC<Props> = ({
  adUnit,
  divId,
  scope,
  sizes,
  sizeMapping,
  stuck,
  sticky,
  autoRefresh,
  root,
}: Props) => {
  const slotId = divId || adUnitToDivId(adUnit, scope);
  const [isFilled, setIsFilled] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);

  // refresh responsive ads on resize
  useEffect(() => {
    if (sizeMapping) {
      const breakpoints = sizeMapping
        .map(m => m.viewport[0])
        .filter(w => w > 0)
        .sort()
        .reverse();

      let timeoutId: number;

      const resizeListener = () => {
        clearTimeout(timeoutId);
        timeoutId = window.setTimeout(() => {
          // loops array until true is returned
          breakpoints.some(breakpoint => {
            // crossed breakpoint from bigger to smaller OR from smaller to bigger
            if (
              (viewportWidth > breakpoint && window.innerWidth <= breakpoint) ||
              (viewportWidth < breakpoint && window.innerWidth >= breakpoint)
            ) {
              DFPManager.refresh(slotId);
              return true;
            }
            return false;
          });
          setViewportWidth(window.innerWidth);
        }, 500);
      };

      window.addEventListener('resize', resizeListener);

      return () => {
        window.removeEventListener('resize', resizeListener);
      };
    }
  }, []);

  // auto refresh
  useEffect(() => {
    let interval: number;

    if (autoRefresh) {
      interval = window.setInterval(() => {
        if (isViewable(slotId)) DFPManager.refresh(slotId);
      }, 30000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [slotId, autoRefresh]);

  // cleanup
  useEffect(
    () => () => {
      // console.log('destroying', slotId);
      DFPManager.destroyGPTSlots(slotId);
    },
    [slotId]
  );

  const dom = useMemo(
    () => (
      <div
        className={`Ad ${sticky ? 'Ad--sticky' : ''} ${stuck ? 'Ad--stuck' : ''} ${
          isFilled ? 'Ad--filled' : ''
        }`}
      >
        <AdSlot
          adUnit={adUnit}
          slotId={slotId}
          sizes={sizes}
          sizeMapping={sizeMapping}
          onSlotRender={data => {
            if (!data.event.isEmpty) {
              setIsFilled(true);
            } else setIsFilled(false);
          }}
        />
      </div>
    ),
    [adUnit, isFilled, sizeMapping, sizes, slotId, sticky, stuck]
  );

  // if portal is used but non-existent, don't run element;
  if (root === null) return null;
  if (root === undefined) return dom;
  return root && createPortal(dom, root);
};

export default React.memo(Ad);
